import React, { useEffect, useState } from "react";
import { parentalApi } from "./parentalApi";
import { money } from "./AllowanceEditor";

// ---------------------------------------------------------------------------------------------------------------------------------
// Parental Control 13+, sheet row 1.4.3: "Parent activity summary and alerts" (the summary half).
//
// Shown to a PARENT on the Family page, under "Linked teens": for each teen, the last 7 days at a glance. The numbers are worked out by
// the server (parental-summary.service.js): time on the site per day, the busiest day, how many days the teen reached the daily limit,
// when the teen was last on, and this month's spending. This page only shows them. The alerts half of the row (time threshold, limit
// reached, allowance refused) arrives as notices, push messages and emails, and the same facts are sent once a week as an email.
// ---------------------------------------------------------------------------------------------------------------------------------

// 95 -> "1 h 35 min", 40 -> "40 min", 0 -> "0 min"
export function duration(minutes) {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

const dayLabel = (day) => {
  const d = new Date(`${day}T12:00:00`);
  return Number.isNaN(d.getTime()) ? day : d.toLocaleDateString(undefined, { weekday: "short" });
};
const lastSeen = (value) => (value ? new Date(value).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "Not yet");

function Bars({ days }) {
  // oldest first, so the bars read left to right in time
  const ordered = [...days].reverse();
  const top = Math.max(1, ...ordered.map((d) => d.minutes));
  return (
    <div className="mt-3 flex items-end gap-2" role="img" aria-label="Minutes on the site for each of the last 7 days">
      {ordered.map((d) => (
        <div key={d.day} className="flex flex-1 flex-col items-center">
          <div className="flex h-16 w-full items-end">
            <div className="w-full rounded-t bg-gray-300" style={{ height: `${Math.max(d.minutes ? 6 : 2, (d.minutes / top) * 100)}%` }} title={`${d.day}: ${duration(d.minutes)}`} />
          </div>
          <span className="mt-1 text-[11px] text-gray-500">{dayLabel(d.day)}</span>
        </div>
      ))}
    </div>
  );
}

export default function WeekSummary() {
  const [state, setState] = useState({ status: "loading", children: [] });
  useEffect(() => {
    let alive = true;
    parentalApi.summary()
      .then((r) => { if (alive) setState({ status: "ready", children: r?.children || [] }); })
      .catch(() => { if (alive) setState({ status: "error", children: [] }); });
    return () => { alive = false; };
  }, []);

  if (state.status !== "ready" || state.children.length === 0) return null; // nothing to show is not an error worth a message here
  return (
    <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="week-summary">
      <h2 className="text-base font-semibold text-gray-900">This week</h2>
      <p className="mt-1 text-xs text-gray-500">The last 7 days. You also get this by email every Monday.</p>
      <ul className="mt-3">
        {state.children.map((s) => {
          const w = s.week;
          return (
            <li key={s.child?.id} className="border-b border-gray-100 py-4 last:border-0" data-testid="week-summary-teen">
              <p className="text-sm font-semibold text-gray-900">{s.child?.name}</p>
              <p className="mt-1 text-sm text-gray-700">
                {duration(w.total_minutes)} in total, about {duration(w.average_minutes)} a day, on {w.active_days} of 7 days.
              </p>
              <Bars days={w.days} />
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 sm:grid-cols-4">
                <div><dt className="text-gray-400">Busiest day</dt><dd>{w.busiest_day ? `${dayLabel(w.busiest_day.day)}, ${duration(w.busiest_day.minutes)}` : "None"}</dd></div>
                <div><dt className="text-gray-400">Reached the daily limit</dt><dd>{s.limits?.daily_minutes ? `${w.days_at_limit} of 7 days` : "No daily limit set"}</dd></div>
                <div><dt className="text-gray-400">Last on the site</dt><dd>{lastSeen(w.last_active_at)}</dd></div>
                <div><dt className="text-gray-400">Spent this month</dt><dd>{s.spending ? `${money(s.spending.spent)}${s.spending.monthly_amount === null ? "" : ` of ${money(s.spending.monthly_amount)}`}` : "Not available"}</dd></div>
              </dl>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
